import "./toolbar.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";

import timeoutRates from "../../constants/timeoutRates";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { getPointerPositionInDOMElement, getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";
import { withOptionalTooltip } from "../interface/Tooltip";
import onPulseAnimationEnd from "../onPulseAnimationEnd";

const clickWasKnob = e => hasAncestorOfClass(e.target, "toolbarSliderKnob");

const logarithmicPositionToValue = (pos, minValue, maxValue) => {
  const s = Math.pow(maxValue / minValue, 0.01);
  const percentage = pos * 100;
  const value = minValue * Math.pow(s, percentage);
  return value;
};

const valueToLogarithmicPosition = (value, minValue, maxValue) => {
  const s = Math.pow(maxValue / minValue, 0.01);
  const pos = Math.log(value / minValue) / Math.log(s);
  return pos / 100;
};

const linearPositionToValue = (pos, minValue, maxValue) => {
  const percentage = Math.max(0.0, Math.min(1.0, pos));
  return minValue + percentage * (maxValue - minValue);
};

const valueToLinearPosition = (value, minValue, maxValue) => {
  return (value - minValue) / (maxValue - minValue);
};

class ToolbarSlider extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.onKnobMouseDown = this.onKnobMouseDown.bind(this);
    this.onKnobMouseMove = this.onKnobMouseMove.bind(this);
    this.onKnobMouseUp = this.onKnobMouseUp.bind(this);
    this.onTrackClick = this.onTrackClick.bind(this);
    this._domNode = null;

    this.throttledOnKnobMouseMove = _.throttle(this.onKnobMouseMove, timeoutRates.SLIDER_THROTTLE, {
      leading: false,
      trailing: true
    });
  }

  componentWillMount() {
    this.updateKnobLocation(this.props, this.props.value);
  }

  componentWillReceiveProps(nextProps) {
    this.updateKnobLocation(nextProps, nextProps.value);
  }

  updateKnobLocation(props, value) {
    let percentage = props.logarithmic
      ? valueToLogarithmicPosition(value, props.minValue, props.maxValue)
      : valueToLinearPosition(value, props.minValue, props.maxValue);
    percentage = Math.min(Math.max(0, percentage), 1);
    const knobLocation = percentage * props.width;
    this.setState({
      knobLocation: knobLocation
    });
  }

  onKnobMouseDown(e) {
    document.addEventListener("mousemove", this.throttledOnKnobMouseMove);
    document.addEventListener("mouseup", this.onKnobMouseUp);
    document.addEventListener("touchmove", this.throttledOnKnobMouseMove);
    document.addEventListener("touchend", this.onKnobMouseUp);
    this._mouseDownX = getScreenPointerPositionFromEvent(e).x;
    this._mouseDownValue = this.props.value;
    this._mouseDownPercentage = this.props.logarithmic
      ? valueToLogarithmicPosition(this.props.value, this.props.minValue, this.props.maxValue)
      : valueToLinearPosition(this.props.value, this.props.minValue, this.props.maxValue);
    this._mouseDownKnobLocation = this._mouseDownPercentage * this.props.width;
  }

  onKnobMouseMove(e) {
    const x = getScreenPointerPositionFromEvent(e).x;
    const dist = x - this._mouseDownX;
    const dragKnobLocation = this._mouseDownKnobLocation + dist;
    const percentage = Math.max(0.0, Math.min(1.0, dragKnobLocation / this.props.width));

    this._mouseMoveValue = this.props.logarithmic
      ? logarithmicPositionToValue(percentage, this.props.minValue, this.props.maxValue)
      : linearPositionToValue(percentage, this.props.minValue, this.props.maxValue);

    this.updateKnobLocation(this.props, this._mouseMoveValue);

    if (this.props.liveChange && this.props.onChange) {
      this.props.onChange(this._mouseMoveValue);
    }
  }

  onKnobMouseUp(_e) {
    document.removeEventListener("mousemove", this.throttledOnKnobMouseMove);
    document.removeEventListener("mouseup", this.onKnobMouseUp);
    document.removeEventListener("touchmove", this.throttledOnKnobMouseMove);
    document.removeEventListener("touchend", this.onKnobMouseUp);
    if (!this.props.liveChange && this.props.onChange) {
      this.props.onChange(this._mouseMoveValue);
    }
  }

  onTrackClick(e) {
    // Ignore any mouse clicks that hit the knob itself
    if (clickWasKnob(e)) {
      return;
    }

    // Otherwise, jump to the location clicked
    const x = getPointerPositionInDOMElement(e, this._domNode).x;
    const percentage = Math.max(0.0, Math.min(1.0, x / this.props.width));
    const value = this.props.logarithmic
      ? logarithmicPositionToValue(percentage, this.props.minValue, this.props.maxValue)
      : linearPositionToValue(percentage, this.props.minValue, this.props.maxValue);

    this.updateKnobLocation(this.props, value);

    if (this.props.onChange) {
      this.props.onChange(value);
    }
  }

  render() {
    let className = ["toolbarSlider"];
    if (this.props.pulse) {
      className.push("pulse");
    }

    const sliderStyle = {
      width: `${this.props.width}px`
    };

    const knobStyle = {
      transform: `translateX(${this.state.knobLocation}px)`
    };

    const slider = (
      <div
        className={className.join(" ")}
        style={sliderStyle}
        onClick={this.onTrackClick}
        ref={node => (this._domNode = node)}
        onAnimationEnd={onPulseAnimationEnd}
      >
        <div className="toolbarSliderTrack" />
        <div className="toolbarSliderKnob" style={knobStyle} onMouseDown={this.onKnobMouseDown} />
      </div>
    );

    return withOptionalTooltip(slider, this.props.tooltip, { placement: "top", offset: "0, 14" });
  }
}

ToolbarSlider.propTypes = {
  width: PropTypes.number.isRequired,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  logarithmic: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  liveChange: PropTypes.bool,
  tooltip: PropTypes.string,
  pulse: PropTypes.bool
};

export default ToolbarSlider;
